import { inject, Injectable } from '@angular/core';
import { applyTransaction } from '@datorama/akita';
import { METADATA_JSON_SCHEMA_URL, Y42MetadataGitModel } from '@y42/feature/json-schema';
import { ImpexChildService } from '@y42/impex';
import { Y42FeatureType } from '@y42/json-schema';
import { getSafeString } from '@y42/util/safe-string';
import {
  ExportQuery,
  ExportStore,
  ExportStoreModel,
  ExportTriggerGitFactoryService,
  ExportTriggerQuery,
  ExportTriggerState,
  ExportTriggerStore,
  ExportTriggerStoreModel,
} from '../../state';

@Injectable({ providedIn: 'root' })
export class ExportTriggerService extends ImpexChildService<ExportTriggerState> {
  constructor(
    private readonly parentStore: ExportStore,
    private readonly parentQuery: ExportQuery,
    private readonly query: ExportTriggerQuery,
    private readonly store: ExportTriggerStore,
  ) {
    super({
      feature: Y42FeatureType.Export,
      gitFactory: inject(ExportTriggerGitFactoryService),
      parentStore,
      parentQuery,
      store,
      query,
    });
  }

  async create(item: { name: string; settings: NonNullable<Omit<ExportTriggerStoreModel['settings'], '$schema'>> }) {
    const createdTrigger = applyTransaction(() => {
      const { name, settings } = item;
      const exxport = this.parentQuery.getActive()! as ExportStoreModel;
      const exportName = exxport.$name;
      const trigger = this.store.create({
        ...new Y42MetadataGitModel(),
        $schema: METADATA_JSON_SCHEMA_URL,
        $name: getSafeString(name),
        name,
        settings: {
          ...settings,
          $schema: `${exxport.settings.$schema.split('/').slice(0, -1).join('/')}/table.json`,
        },
        $parentFolders: [exportName],
      });

      this.parentStore.update(
        trigger.$parent,
        (st): Partial<ExportStoreModel> => ({ $children: [...(st.$children || []), trigger.$path] }),
      );

      return trigger;
    });

    await Promise.all([this.parentStore.pending, this.store.pending]);

    return createdTrigger;
  }

  async update(
    trigger: Partial<ExportTriggerStoreModel>,
    $path: ExportTriggerStoreModel['$path'] = this.query.getActiveId()!,
  ) {
    if (!$path) {
      throw new Error(`[Export]: "${$path}" not found.`);
    }

    this.store.update($path, trigger);
    await this.store.pending;
  }

  async delete($path: ExportStoreModel['$path'] = this.query.getActiveId()!) {
    applyTransaction(() => {
      this.store.delete($path);
      this.parentStore.update($path, (st) => ({
        $children: st.$children.filter((child) => child !== $path),
      }));
    });
    await Promise.all([this.store.pending, this.parentStore.pending]);
  }

  async hasSettings(): Promise<boolean> {
    return false;
  }

  async editSettings(): Promise<void> {
    return;
  }
}
